import type OwaUserConfiguration from 'owa-service/lib/contract/OwaUserConfiguration';
import { UserConfigurationArgs, UserConfiguration, DEFAULT_USER_CONFIG } from 'owa-graph-schema';
import * as trace from 'owa-trace';
import { isRunningOnWorker } from 'owa-config';
import { convertUserConfigToGraphQLSchema } from './utils/convertUserConfigToGraphQLSchema';
import merge from 'lodash-es/merge';

export type OwaUserConfigurationProxy = (
    userConfigId?: string | null,
    updates?: UserConfigurationArgs
) => Promise<UserConfiguration>;

const cache = new Map<string, UserConfiguration>();
let proxy: OwaUserConfigurationProxy;

export function setConfiguration(
    configuration: OwaUserConfiguration | OwaUserConfigurationProxy | undefined,
    userConfigId?: string
) {
    userConfigId = userConfigId || DEFAULT_USER_CONFIG;

    if (isProxy(configuration)) {
        proxy = configuration;
    } else if (isRunningOnWorker()) {
        trace.errorThatWillCauseAlert(
            'Cannot set configuration on worker.  Configuration should be consumed by the worker via a proxy to the main renderer.'
        );
    } else {
        try {
            if (configuration) {
                const configCopy = JSON.parse(JSON.stringify(configuration));
                const config = convertUserConfigToGraphQLSchema(configCopy, userConfigId);
                cache[userConfigId] = config;
            }
        } catch (e) {
            trace.errorThatWillCauseAlert('User configuration cache set failed: ' + e);
        }
    }
}

export async function getConfiguration(userConfigId?: string | null): Promise<UserConfiguration> {
    if (isRunningOnWorker()) {
        return proxy(userConfigId);
    } else {
        return getConfigurationSync(userConfigId);
    }
}

/**
 * This should be avoided by resolvers and should only be consumed where it's absolutely necessary to get the synchronous result, such
 * as in cache policies.  Most invocations should prefer the getConfiguration method.
 */
export function getConfigurationSync(userConfigId?: string | null): UserConfiguration {
    if (isRunningOnWorker()) {
        trace.errorThatWillCauseAlert(
            'Cannot get configuration synchronously on worker.  Configuration should be consumed by the worker via a proxy to the main renderer.'
        );
    }

    userConfigId = userConfigId || DEFAULT_USER_CONFIG;
    return cache[userConfigId] || { id: userConfigId };
}

export async function updateConfiguration(
    userConfigId?: string | null,
    updates?: UserConfigurationArgs
) {
    if (isRunningOnWorker()) {
        await proxy(userConfigId, updates);
    } else if (updates) {
        userConfigId = userConfigId || DEFAULT_USER_CONFIG;
        merge(cache[userConfigId], updates);
    }
}

function isProxy(
    configuration: OwaUserConfiguration | OwaUserConfigurationProxy | undefined
): configuration is OwaUserConfigurationProxy {
    return typeof configuration === 'function';
}
